import { createContext, useContext, useEffect, useState, ReactNode, useCallback, useMemo } from 'react';

export type Currency =
  | 'USD' | 'EUR' | 'GBP' | 'JPY' | 'CNY' | 'INR' | 'AUD' | 'CAD' | 'CHF'
  | 'HKD' | 'SGD' | 'NZD' | 'SEK' | 'NOK' | 'DKK' | 'KRW' | 'AED' | 'SAR'
  | 'ZAR' | 'BRL' | 'MXN' | 'TRY' | 'RUB' | 'THB' | 'MYR' | 'IDR' | 'PHP'
  | 'PLN' | 'CZK' | 'HUF' | 'ILS' | 'TWD' | 'VND' | 'NGN' | 'EGP' | 'PKR'
  | 'BDT' | 'QAR' | 'KWD' | 'BHD' | 'OMR' | 'JOD' | 'LKR';

export const CURRENCY_SYMBOL: Record<Currency, string> = {
  USD: '$', EUR: '€', GBP: '£', JPY: '¥', CNY: '¥', INR: '₹', AUD: 'A$', CAD: 'C$', CHF: 'CHF',
  HKD: 'HK$', SGD: 'S$', NZD: 'NZ$', SEK: 'kr', NOK: 'kr', DKK: 'kr', KRW: '₩', AED: 'د.إ', SAR: 'ر.س',
  ZAR: 'R', BRL: 'R$', MXN: 'Mex$', TRY: '₺', RUB: '₽', THB: '฿', MYR: 'RM', IDR: 'Rp', PHP: '₱',
  PLN: 'zł', CZK: 'Kč', HUF: 'Ft', ILS: '₪', TWD: 'NT$', VND: '₫', NGN: '₦', EGP: 'E£', PKR: '₨',
  BDT: '৳', QAR: 'ر.ق', KWD: 'د.ك', BHD: '.د.ب', OMR: 'ر.ع.', JOD: 'د.ا', LKR: 'Rs',
};

export const CURRENCY_LABEL: Record<Currency, string> = {
  USD: 'US Dollar', EUR: 'Euro', GBP: 'British Pound', JPY: 'Japanese Yen', CNY: 'Chinese Yuan',
  INR: 'Indian Rupee', AUD: 'Australian Dollar', CAD: 'Canadian Dollar', CHF: 'Swiss Franc',
  HKD: 'Hong Kong Dollar', SGD: 'Singapore Dollar', NZD: 'New Zealand Dollar', SEK: 'Swedish Krona',
  NOK: 'Norwegian Krone', DKK: 'Danish Krone', KRW: 'South Korean Won', AED: 'UAE Dirham',
  SAR: 'Saudi Riyal', ZAR: 'South African Rand', BRL: 'Brazilian Real', MXN: 'Mexican Peso',
  TRY: 'Turkish Lira', RUB: 'Russian Ruble', THB: 'Thai Baht', MYR: 'Malaysian Ringgit',
  IDR: 'Indonesian Rupiah', PHP: 'Philippine Peso', PLN: 'Polish Złoty', CZK: 'Czech Koruna',
  HUF: 'Hungarian Forint', ILS: 'Israeli Shekel', TWD: 'Taiwan Dollar', VND: 'Vietnamese Dong',
  NGN: 'Nigerian Naira', EGP: 'Egyptian Pound', PKR: 'Pakistani Rupee', BDT: 'Bangladeshi Taka',
  QAR: 'Qatari Riyal', KWD: 'Kuwaiti Dinar', BHD: 'Bahraini Dinar', OMR: 'Omani Rial',
  JOD: 'Jordanian Dinar', LKR: 'Sri Lankan Rupee',
};

export const CURRENCY_LIST: Currency[] = Object.keys(CURRENCY_LABEL) as Currency[];

interface FxState {
  /** Multipliers from USD → target. USD is always 1. */
  rates: Record<Currency, number>;
  fetchedAt: number | null;
  loading: boolean;
  error: string | null;
}

interface Ctx extends FxState {
  currency: Currency;
  symbol: string;
  setCurrency: (c: Currency) => void;
  /** Multiplier from USD → currently selected currency. */
  toSelected: number;
  /** Format a USD amount in the currently selected currency. */
  format: (usd: number, opts?: Intl.NumberFormatOptions) => string;
  /** Format an amount that's ALREADY in the selected currency. */
  formatLocal: (amount: number, opts?: Intl.NumberFormatOptions) => string;
  refreshFx: () => Promise<void>;
}

const CURRENCY_KEY = 'aurum_currency_v1';
const FX_KEY = 'aurum_fx_v2';
const FX_TTL_MS = 1000 * 60 * 60 * 6;

// Static fallback FX (approx Q4 2025). Used only if the live FX API is unreachable.
const FALLBACK_FX: Record<Currency, number> = {
  USD: 1, EUR: 0.92, GBP: 0.79, JPY: 155, CNY: 7.25, INR: 83.5, AUD: 1.52, CAD: 1.36, CHF: 0.88,
  HKD: 7.81, SGD: 1.34, NZD: 1.66, SEK: 10.6, NOK: 10.7, DKK: 6.85, KRW: 1380, AED: 3.67, SAR: 3.75,
  ZAR: 18.5, BRL: 5.4, MXN: 18.0, TRY: 34.5, RUB: 92, THB: 35.5, MYR: 4.7, IDR: 15800, PHP: 57,
  PLN: 4.0, CZK: 23.0, HUF: 360, ILS: 3.7, TWD: 32.0, VND: 25400, NGN: 1600, EGP: 49, PKR: 280,
  BDT: 119, QAR: 3.64, KWD: 0.31, BHD: 0.38, OMR: 0.385, JOD: 0.71, LKR: 300,
};

const CurrencyContext = createContext<Ctx | null>(null);

interface CachedFx { rates: Record<Currency, number>; ts: number }

function loadFxCache(): CachedFx | null {
  try {
    const raw = localStorage.getItem(FX_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch { return null; }
}

export function CurrencyProvider({ children }: { children: ReactNode }) {
  const [currency, setCurrencyState] = useState<Currency>('USD');
  const [fx, setFx] = useState<FxState>({
    rates: FALLBACK_FX,
    fetchedAt: null,
    loading: false,
    error: null,
  });

  // Load persisted currency + cached FX
  useEffect(() => {
    try {
      const c = localStorage.getItem(CURRENCY_KEY) as Currency | null;
      if (c && CURRENCY_LIST.includes(c)) setCurrencyState(c);
    } catch { /* noop */ }
    const cached = loadFxCache();
    if (cached) {
      // Merge cached over fallback so missing currencies still resolve.
      setFx(s => ({ ...s, rates: { ...FALLBACK_FX, ...cached.rates }, fetchedAt: cached.ts }));
    }
  }, []);

  const refreshFx = useCallback(async () => {
    // Skip network if cache is fresh
    const cached = loadFxCache();
    if (cached && Date.now() - cached.ts < FX_TTL_MS) {
      setFx({ rates: { ...FALLBACK_FX, ...cached.rates }, fetchedAt: cached.ts, loading: false, error: null });
      return;
    }
    setFx(s => ({ ...s, loading: true, error: null }));
    try {
      const symbols = CURRENCY_LIST.filter(c => c !== 'USD').join(',');
      // Free, no-key, CORS-friendly. Falls back gracefully if unreachable.
      const resp = await fetch(`https://api.exchangerate.host/latest?base=USD&symbols=${symbols}`);
      if (!resp.ok) throw new Error(`FX API ${resp.status}`);
      const json = await resp.json();
      const apiRates = json?.rates || {};
      const rates: Record<Currency, number> = { ...FALLBACK_FX, USD: 1 };
      let validCount = 0;
      for (const c of CURRENCY_LIST) {
        if (c === 'USD') continue;
        const v = Number(apiRates[c]);
        if (Number.isFinite(v) && v > 0) { rates[c] = v; validCount++; }
      }
      if (validCount === 0) throw new Error('Invalid FX response');
      const ts = Date.now();
      localStorage.setItem(FX_KEY, JSON.stringify({ rates, ts }));
      setFx({ rates, fetchedAt: ts, loading: false, error: null });
    } catch (e: any) {
      // Keep last known good (or static fallback) — calculations keep working.
      setFx(s => ({ ...s, loading: false, error: e?.message ?? 'FX fetch failed' }));
    }
  }, []);

  useEffect(() => { refreshFx(); }, [refreshFx]);

  const setCurrency = useCallback((c: Currency) => {
    setCurrencyState(c);
    try { localStorage.setItem(CURRENCY_KEY, c); } catch { /* noop */ }
  }, []);

  const toSelected = fx.rates[currency] ?? 1;
  const symbol = CURRENCY_SYMBOL[currency];

  const format = useCallback((usd: number, opts?: Intl.NumberFormatOptions) => {
    const value = usd * toSelected;
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
      ...opts,
    }).format(value);
  }, [currency, toSelected]);

  const formatLocal = useCallback((amount: number, opts?: Intl.NumberFormatOptions) => {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
      ...opts,
    }).format(amount);
  }, [currency]);

  const value = useMemo<Ctx>(() => ({
    ...fx,
    currency,
    symbol,
    setCurrency,
    toSelected,
    format,
    formatLocal,
    refreshFx,
  }), [fx, currency, symbol, setCurrency, toSelected, format, formatLocal, refreshFx]);

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

export function useCurrency() {
  const ctx = useContext(CurrencyContext);
  if (!ctx) throw new Error('useCurrency must be used inside CurrencyProvider');
  return ctx;
}

/**
 * Apply a USD→currency multiplier to a LiveRates object so
 * `calculatePieceMarketPrice` produces values in the selected currency.
 */
export function convertRates<T extends {
  goldPerGram: Record<string, number>;
  silverPerGram: number;
  gemPerCarat: Record<string, number | undefined>;
}>(rates: T | null, multiplier: number): T | null {
  if (!rates) return null;
  if (multiplier === 1) return rates;
  const goldPerGram: Record<string, number> = {};
  for (const [k, v] of Object.entries(rates.goldPerGram)) goldPerGram[k] = v * multiplier;
  const gemPerCarat: Record<string, number | undefined> = {};
  for (const [k, v] of Object.entries(rates.gemPerCarat)) {
    gemPerCarat[k] = v != null ? v * multiplier : v;
  }
  return {
    ...rates,
    goldPerGram,
    silverPerGram: rates.silverPerGram * multiplier,
    gemPerCarat,
  };
}
